import { useEffect, useState } from 'react';
import { IconButton } from '@dugzi/ui';
import { CheckCircle2, ChevronDown, ChevronUp, RotateCcw, X, XCircle } from 'lucide-react';
import { bytes } from '../lib/format';
import * as uploads from '../lib/uploads';

/** Uploads in progress, bottom right: progress, cancel, retry. Out of the way of working on. */
export function UploadPanel() {
  const [items, setItems] = useState<uploads.UploadItem[]>([]);
  const [open, setOpen] = useState(true);
  useEffect(() => uploads.subscribe(setItems), []);
  if (!items.length) return null;
  const running = items.filter((i) => i.state === 'uploading' || i.state === 'queued');
  const failed = items.filter((i) => i.state === 'failed');
  const total = running.reduce((s, i) => s + i.size, 0);
  const sent = running.reduce((s, i) => s + i.sent, 0);
  const title = running.length ? `Uploading ${running.length} ${running.length === 1 ? 'file' : 'files'}` : failed.length ? `${failed.length} ${failed.length === 1 ? 'upload' : 'uploads'} failed` : 'Uploads done';
  return (
    <section className="uploads" aria-label="Uploads" data-testid="uploads">
      <div className="uploads__head">
        <h2 aria-live="polite">{title}{running.length > 0 && total > 0 ? ` · ${Math.floor((sent / total) * 100)}%` : ''}</h2>
        <IconButton label={open ? 'Hide uploads' : 'Show uploads'} icon={open ? <ChevronDown aria-hidden size={18} /> : <ChevronUp aria-hidden size={18} />} onClick={() => setOpen((o) => !o)} />
        {!running.length && <IconButton label="Close uploads" icon={<X aria-hidden size={18} />} onClick={() => uploads.clearFinished()} data-testid="uploads-close" />}
      </div>
      {open && (
        <ul className="uploads__list">
          {items.slice(-50).map((i) => (
            <li key={i.key} className={`up up--${i.state}`} data-testid="upload-item" data-state={i.state} data-name={i.name}>
              <span className="up__name">{i.path ? `${i.path}/` : ''}{i.name}</span>
              <span className="up__meta">
                {i.state === 'uploading' && `${bytes(i.sent)} of ${bytes(i.size)}`}
                {i.state === 'queued' && 'Waiting'}
                {i.state === 'done' && <><CheckCircle2 aria-hidden size={16} className="ok" /><span className="sr-only">Done</span></>}
                {i.state === 'failed' && <span className="err"><XCircle aria-hidden size={16} />{i.error}</span>}
                {i.state === 'cancelled' && 'Cancelled'}
              </span>
              {(i.state === 'uploading' || i.state === 'queued') && <>
                <span className="up__bar" aria-hidden="true"><span style={{ width: `${i.size ? (i.sent / i.size) * 100 : 0}%` }} /></span>
                <IconButton label={`Cancel ${i.name}`} icon={<X aria-hidden size={16} />} onClick={() => uploads.cancel(i.key)} data-testid="upload-cancel" />
              </>}
              {(i.state === 'failed' || i.state === 'cancelled') && <IconButton label={`Try ${i.name} again`} icon={<RotateCcw aria-hidden size={16} />} onClick={() => uploads.retry(i.key)} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
